import { SportType } from 'modules/core/domain/enums/sport-type';
import { OrganizerListItem } from 'modules/organizers/domain/interfaces/organizer-list-item';
import { mapTournamentSort } from 'modules/organizers/domain/utils/map-tournament-sort';
import { ListOrganizersArgs, useListOrganizersQuery } from 'modules/organizers/store/organizers-api';
import { useMemo } from 'react';
import { ListFilterParamDef, useListFilterParams, usePagedList } from 'shared/hooks/paged-list';

/** Ключ localStorage для `perPage`: размер страницы запоминается между визитами. */
const STORAGE_KEY = 'organizers';
const DEFAULT_PER_PAGE = 9;
/** Организаций много, и поиск по одной букве бессмысленно нагружает бэкенд. */
const MIN_SEARCH_LENGTH = 3;
const DEFAULT_SORT = 'DESC';

/** Доп. фильтры страницы. Ссылка стабильная — набор объявлен константой модуля. */
const FILTER_PARAMS = {
  phone: { debounceMs: 400 },
  sportType: {},
  sort: { defaultValue: DEFAULT_SORT, narrows: false },
} satisfies Record<string, ListFilterParamDef>;

type OrganizersFilterKey = keyof typeof FILTER_PARAMS;

const mapSearchToArgs = (search: string): Partial<ListOrganizersArgs> => ({ text: search || undefined });

/**
 * Список организаций: страница, размер страницы, поиск и доп. фильтры живут в URL,
 * данные — в RTK Query.
 *
 * Раньше нумерация считалась `use-block-pager-core` и хранилась в module-level
 * `Map`, переживая unmount; теперь она внутри `usePagedList`. Поиск и телефон
 * ходили в URL на каждое нажатие клавиши — теперь их держит дебаунс.
 */
export function useOrganizersList() {
  const { values, drafts, setFilter, hasActiveFilters } = useListFilterParams<OrganizersFilterKey>(FILTER_PARAMS);

  const baseArgs = useMemo<Partial<ListOrganizersArgs>>(
    () => ({
      phone: values.phone || undefined,
      sportType: values.sportType ? (values.sportType.toUpperCase() as SportType) : undefined,
      ...mapTournamentSort(values.sort),
    }),
    [values.phone, values.sportType, values.sort],
  );

  const list = usePagedList<OrganizerListItem, ListOrganizersArgs>({
    useQueryHook: useListOrganizersQuery,
    baseArgs,
    mapSearchToArgs,
    storageKey: STORAGE_KEY,
    defaultPerPage: DEFAULT_PER_PAGE,
    minSearchLength: MIN_SEARCH_LENGTH,
  });

  return { ...list, filters: values, filterDrafts: drafts, setFilter, hasActiveFilters };
}
